import { useMemo, useRef, useState } from "react";
import type { Exercise } from "@jackedlogs/shared";

import type { GroupOption } from "../../ExercisePicker/ExercisePicker";
import type { LoggerProfile } from "../../Logger/hostDeps";
import type { AnimationSourceFn } from "../../Logger/ExerciseVisualPager";
import type { LoggerDispatch } from "../../Logger/state/loggerDraft";
import type {
  DraftEntry,
  LoggerDraft,
  WeightUnit,
} from "../../Logger/state/loggerDraftTypes";
import { useAddExercise } from "../../Logger/useAddExercise";
import type { usePlateCalc } from "../../Logger/usePlateCalc";
import type { useRestPicker } from "../../Logger/useRestPicker";
import type { useTimerDefaults } from "../../Logger/useTimerDefaults";

export type PrescribeModalStateArgs = {
  /** usePrescribeMount's K1 read adapter — read here for its live
   *  entries/location (useAddExercise's silent-instance resolver), never
   *  mutated. */
  draft: LoggerDraft;
  /** The K2.2 draft-direct dispatch adapter (usePrescribeMount.ts:211-214).
   *  useAddExercise's add_exercise / seed_new_entry_sets / set_group /
   *  align_group_sets all land through the SAME setDraft this mount already
   *  writes cells through — no second reducer, no second save-path. */
  dispatch: LoggerDispatch;
  defaultUnit: WeightUnit;
  catalog: Exercise[] | null;
  profile: LoggerProfile;
};

/** PE-26 — the modal-open state the card's six affordances (＋/swap/tip/
 *  group/superset/effort-scale) write into, plus the add-exercise wiring
 *  `<LoggerModals>` needs to act on them. These used to be blind
 *  `usePrescribeMount` setters with their value thrown away (K2.1) — this
 *  hook is the fix, not just the value: it also builds `addMany`/
 *  `createSuperset` off the SAME dispatch, so a card tap now actually adds
 *  an exercise to the program draft instead of doing nothing.
 *
 *  Extracted out of usePrescribeMount (which would clear the 460-line soft
 *  cap with this inline) — same reasoning K3.2/K3.3/K3.4 already used for
 *  usePrescribeSetCommands.ts. */
export function usePrescribeModalState({
  draft,
  dispatch,
  defaultUnit,
  catalog,
  profile,
}: PrescribeModalStateArgs) {
  // Latest-draft ref for useAddExercise's silent-instance resolver, which
  // reads draftRef.current.location/.entries at dispatch time (a picker adds
  // N exercises in a loop) rather than at render time — mirrors
  // useDraftPersistence's draftRef in the real Logger.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [swapEntryIdx, setSwapEntryIdx] = useState<number | null>(null);
  const [effortScaleOpen, setEffortScaleOpen] = useState(false);
  const [tipEntryIdx, setTipEntryIdx] = useState<number | null>(null);
  const [groupPickerEntryIdx, setGroupPickerEntryIdx] = useState<number | null>(
    null,
  );
  const [supersetMenu, setSupersetMenu] = useState<{
    groupId: string;
    entryIdx: number;
  } | null>(null);
  // Not one of the card's six affordances (SupersetMenuSheet's own "add
  // exercise to this group" ☰ action sets it) — LoggerModals still requires
  // it to route the picker into "add to existing group" mode, same as the
  // real Logger's useGroupManagement.
  const [addToGroupId, setAddToGroupId] = useState<string | null>(null);

  const { addMany, createSuperset } = useAddExercise({
    dispatch,
    defaultUnit,
    setPickerOpen,
    draftRef,
    catalog,
    ...(profile.locationUnavailable
      ? { locationUnavailable: profile.locationUnavailable }
      : {}),
    ...(profile.equipmentDefault
      ? { equipmentDefault: profile.equipmentDefault }
      : {}),
  });

  const usedSlugs = useMemo(
    () => draft.entries.map((e) => e.exerciseId),
    [draft.entries],
  );

  // ⛔ NOTHING is wired for the two profile/template writers, and that is the
  // point (PE-26 review, Critical). A program editor has no durable-write
  // authority, and the first cut satisfied LoggerModals' contract with no-op
  // adapters — which made both controls REACHABLE and silently successful:
  // the superset ☰ offered "Save as template" and the picker's ★ pinned a
  // default place, each resolving fine and persisting nothing. That is the
  // same mute-tap defect this slice exists to remove, re-created one layer
  // down. Both props are now optional on LoggerModals, so passing nothing
  // makes the affordances DISAPPEAR — LoggerGymChip's own rule: no door, no ★.

  return {
    pickerOpen,
    setPickerOpen,
    swapEntryIdx,
    setSwapEntryIdx,
    effortScaleOpen,
    setEffortScaleOpen,
    tipEntryIdx,
    setTipEntryIdx,
    groupPickerEntryIdx,
    setGroupPickerEntryIdx,
    supersetMenu,
    setSupersetMenu,
    addToGroupId,
    setAddToGroupId,
    addMany,
    createSuperset,
    usedSlugs,
  };
}

/** The rest of what `<LoggerModals>` needs, beyond the state above — pieces
 *  usePrescribeMount already builds for the card itself (dispatch/plate/
 *  timerDefaults/groupCatalog/the idle restPicker) and has no reason to own
 *  twice. A plain function, not a hook: pure assembly, called once at the
 *  end of usePrescribeMount's body where every input is already in scope. */
export function buildLoggerModalsProps(
  modalState: ReturnType<typeof usePrescribeModalState>,
  extras: {
    catalog: Exercise[] | null;
    entries: DraftEntry[];
    dispatch: LoggerDispatch;
    plate: ReturnType<typeof usePlateCalc>;
    restPicker: ReturnType<typeof useRestPicker>;
    timerDefaults: ReturnType<typeof useTimerDefaults>;
    groupCatalog: ReadonlyArray<GroupOption>;
    defaultUnit: WeightUnit;
    /** PE-33 — the host's GIF resolver. `LoggerModals` already forwards it to
     *  `EntryTipModal` (LoggerModals.tsx:437); it simply was never put in this
     *  bag, so the prescribe "?" showed the muscle map alone. */
    animationSource?: AnimationSourceFn;
  },
) {
  return {
    pickerOpen: modalState.pickerOpen,
    setPickerOpen: modalState.setPickerOpen,
    addMany: modalState.addMany,
    createSuperset: modalState.createSuperset,
    usedSlugs: modalState.usedSlugs,
    swapEntryIdx: modalState.swapEntryIdx,
    setSwapEntryIdx: modalState.setSwapEntryIdx,
    effortScaleOpen: modalState.effortScaleOpen,
    setEffortScaleOpen: modalState.setEffortScaleOpen,
    tipEntryIdx: modalState.tipEntryIdx,
    setTipEntryIdx: modalState.setTipEntryIdx,
    animationSource: extras.animationSource,
    // No usePlanCues instance exists for an authored-not-started draft
    // (usePrescribeMount's `activeCues: undefined`) — the "?" tip's
    // plan-instruction line is blank, same absence as everywhere else here.
    tipPlanNote: undefined,
    groupPickerEntryIdx: modalState.groupPickerEntryIdx,
    setGroupPickerEntryIdx: modalState.setGroupPickerEntryIdx,
    supersetMenu: modalState.supersetMenu,
    setSupersetMenu: modalState.setSupersetMenu,
    addToGroupId: modalState.addToGroupId,
    setAddToGroupId: modalState.setAddToGroupId,
    catalog: extras.catalog,
    entries: extras.entries,
    // No PR history / stored maxes exist for a prescription (usePrescribeMount's
    // cycleSetPill call) — same "not loaded" fallback.
    currentMaxes: null,
    // A program Draft carries no session-level place: LoggerDraft.location is
    // a LOGGED session's fact (EQ-1 slice D), and nothing here is logged.
    location: null,
    // PE-31 — and neither is the MACHINE. `SlotDraft` has no
    // `equipmentInstance`, so the swap picker's machine band is not drawn here
    // at all rather than offered and quietly dropped on commit. Same rule the
    // ★ and "Save as template" already follow above: no door, no control.
    canTagMachine: false,
    dispatch: extras.dispatch,
    plate: extras.plate,
    restPicker: extras.restPicker,
    timerDefaults: extras.timerDefaults,
    groupCatalog: extras.groupCatalog,
    defaultUnit: extras.defaultUnit,
  };
}
